import React, { useState, useRef, useContext, useEffect } from "react";
import "./SetupProfile.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { INITIAL, Profile } from "./types";
import { AuthContext, S3CredentialsContext } from "../../contexts";
import { S3CredentialsContextInterface } from "../../contexts";
import { registerUser } from "../../services/registerUserService";
import { registerInitialUser } from "../../services/registerUserService";
import { RpcError } from "grpc-web";
import { SetupProfileMainSettings } from "../../components/SetupProfileSettings/SetupProfileMainSettings";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";

const SetupProfileInitial: React.FC<{
  handleSuccess: () => void;
}> = ({ handleSuccess }): JSX.Element => {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const { isAuthenticated, user } = useAuth0();
  const { getValidAccessToken } = useGetValidAccessToken();
  //Email,username,firstName,lastName useStates
  const [profile, setProfile] = useState<Profile>(INITIAL);
  const [profilePic, setProfilePic] = useState<string | null>(null);
  const [profilePicBlob, setProfilePicBlob] = useState<Blob | null>(null);
  const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
  const profilePicture = useRef<HTMLInputElement | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [errorFirstName, setErrorFirstName] = useState(false);
  const [errorLastName, setErrorLastName] = useState(false);
  const [errorUsername, setErrorUsername] = useState(false);
  const [errorFirstNameEmpty, setErrorFirstNameEmpty] = useState(false);
  const [errorLastNameEmpty, setErrorLastNameEmpty] = useState(false);
  const [errorUsernameEmpty, setErrorUsernameEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [duplicateUsername, setDuplicateUsername] = useState(false);
  const validate = /^(\d|\w)+$/;

  useEffect(() => {
    async function setupInitial() {
      const email = user?.email;
      if (email !== undefined && email.length > 0) {
        const token = await getValidAccessToken();
        await registerInitialUser(email, token);
      }
    }
    setupInitial();
  }, [user, getValidAccessToken]);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const onImageClick = () => {
    if (profilePicture.current) {
      profilePicture.current.click();
    }
  };

  const handleError = (name: string) => {
    switch (name) {
      case "username(stagename)": {
        setErrorUsername(true);
        break;
      }
      case "firstName": {
        setErrorFirstName(true);
        break;
      }
      case "lastName": {
        setErrorLastName(true);
        break;
      }
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.currentTarget;
    if (files && files.length > 0) {
      const blob = new Blob([files[0]], { type: files[0].type });
      const url = URL.createObjectURL(blob);
      url && setProfilePic(url);
      setProfilePicBlob(blob);
      setProfilePicFile(files[0]);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    if (name === "username(stagename)") {
      setErrorUsername(false);
      setErrorUsernameEmpty(false);
      setProfile((prev) => ({ ...prev, username: value }));
    } else {
      if (name === "firstName") {
        setErrorFirstName(false);
        setErrorFirstNameEmpty(false);
      } else if (name === "lastName") {
        setErrorLastName(false);
        setErrorLastNameEmpty(false);
      }
      setProfile((prev) => ({ ...prev, [name]: value }));
    }
    if (!validate.test(value)) {
      handleError(name);
    }
  };

  const handleChangeSkill = (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const tempArr: string[] = [];
    const values = value as Array<string>;
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];
      tempArr.push(tag_curr);
    }

    setProfile((prev) => ({ ...prev, skills: tempArr }));
  };

  const handleChangeGenre = (
    _event: React.SyntheticEvent<Element, Event>,
    value: unknown
  ) => {
    const tempArr: string[] = [];
    const values = value as Array<string>;
    const length = values.length;
    for (let i = 0; i < length; i++) {
      const tag_curr = values[i];
      tempArr.push(tag_curr);
    }
    setProfile((prev) => ({ ...prev, genres: tempArr }));
  };

  const isFirstNameValid = () => {
    if (!profile.firstName) {
      setErrorFirstNameEmpty(true);
      return false;
    } else if (!validate.test(profile.firstName)) {
      setErrorFirstName(true);
      return false;
    }
    return true;
  };

  const isLastNameValid = () => {
    if (!profile.lastName) {
      setErrorLastNameEmpty(true);
      return false;
    } else if (!validate.test(profile.lastName)) {
      setErrorLastName(true);
      return false;
    }
    return true;
  };

  const isUserNameValid = () => {
    if (!profile.username) {
      setErrorUsernameEmpty(true);
      return false;
    } else if (!validate.test(profile.username)) {
      setErrorUsername(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!isFirstNameValid() || !isLastNameValid() || !isUserNameValid()) {
        isFirstNameValid();
        isLastNameValid();
        isUserNameValid();
        return;
      }
      if (profile.firstName && profile.lastName && profile.username) {
        setIsLoading(true);
        const accessToken = await getValidAccessToken();
        await registerUser(
          profile,
          profilePicBlob,
          userId,
          user,
          profilePicFile,
          accessToken,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken
        );
        setIsLoading(false);
        handleSuccess();
        navigate("/pricing-table");
      }
    } catch (error: any) {
      if (error instanceof RpcError) {
        setIsLoading(false);
        console.log(error.code, error.message);
        if (error.code === 6) {
          if (error.message.includes("username already exists")) {
            setDuplicateUsername(true);
            alert("Username already in use.");
          } else if (error.message.includes("email already exists")) {
            alert(
              "This email already has an account, please signin or use a different email."
            );
          }
        } else {
          alert("Error while registering user, please try again.");
        }
      }
    }
  };

  return (
    <>
      <SetupProfileMainSettings
        errorFirstName={errorFirstName}
        profile={profile}
        handleChange={handleChange}
        setErrorFirstName={setErrorFirstName}
        errorFirstNameEmpty={errorFirstNameEmpty}
        errorLastName={errorLastName}
        setErrorLastName={setErrorLastName}
        errorLastNameEmpty={errorLastNameEmpty}
        handleChangeSkill={handleChangeSkill}
        handleChangeGenre={handleChangeGenre}
        profilePic={profilePic}
        onImageClick={onImageClick}
        profilePicture={profilePicture}
        onInputChange={onInputChange}
        handleSubmit={handleSubmit}
        isLoading={isLoading}
        errorUsername={errorUsername}
        duplicateUsername={duplicateUsername}
        setErrorUsername={setErrorFirstName}
        setDuplicateUsername={setDuplicateUsername}
        errorUsernameEmpty={errorUsernameEmpty}
        isAuthenticated={isAuthenticated}
        user={user}
        isChecked={isChecked}
        handleCheckboxChange={handleCheckboxChange}
      />
    </>
  );
};

export default SetupProfileInitial;
